import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { SummaryService } from "@/services/summaryService";
import { getEstimate } from "@/utils/api";
import { Partition } from "@/app/domain";
import { SummaryBlock as SummaryBlockItem } from "@/models/summary";

import { useNewEstimateStore } from "./estimateStore";
import { useDoorsStore } from "./doors.store";
import { usePartitionStore } from "./partition.store";
import { useSpacesStore } from "./spaces.store";
import { useOverdoorStore } from "./overdoor.store";
import { useDeliveryStore } from "./delivery.store";
import { useExtraStore } from "./extra.store";
import { useOverheadStore } from "./overhead.store";
import { useMountsStore } from "./mounts.store";
import { useEstimateReviewStore } from "./estimateReview.store";
import { useInstallationStore } from "./installation.store";
import { useDistanceStore } from "@/modules/shipping/store/distance.store";

import { useDictStore } from "../dictStore";
import { EstimateConverterService } from "@/app/services/legasy-estimate-converter.service";
import { useRoute, useRouter } from "vue-router";

export const useActionsStrore = defineStore(
    "actions",
    () => {
        const router = useRouter();
        const route = useRoute();

        const estimateStore = useNewEstimateStore();
        const spacesStore = useSpacesStore();
        const partitionsStore = usePartitionStore();
        const doorsStore = useDoorsStore();
        const overdoorsStore = useOverdoorStore();
        const deliveryStore = useDeliveryStore();
        const extraStore = useExtraStore();
        const mountsStore = useMountsStore();
        const overheadsStore = useOverheadStore();
        const dictStore = useDictStore();
        const estimateReviewStore = useEstimateReviewStore();
        const installationStore = useInstallationStore();

        const estimate = computed(() => estimateStore.estimate);

        const activeSpace = computed(() => spacesStore.activeSpace);
        const spaces = computed(() => spacesStore.spaces);
        const deliveryItems = computed(() => deliveryStore.deliveryItems);

        const summary = ref<SummaryBlockItem | null>(null);

        const resetAllStores = () => {
            estimateStore.$reset();
            spacesStore.$reset();
            partitionsStore.$reset();
            doorsStore.$reset();
            overdoorsStore.$reset();
            deliveryStore.$reset();
            extraStore.$reset();
            mountsStore.$reset();
            overheadsStore.$reset();
            estimateReviewStore.$reset();
            installationStore.$reset();
        };

        const addAnotherPartition = () => {
            partitionsStore.$reset();
            doorsStore.$reset();
            overdoorsStore.$reset();
            mountsStore.$reset();
        };

        const addAnotherSpace = () => {
            spacesStore.addSpace();
            addAnotherPartition();
        };

        const editPartition = (id: string) => {
            const spaceWithPartition = spaces.value.find((space) => {
                return Boolean(space.getPartitionById(id));
            });

            const partition = spaceWithPartition?.getPartitionById(id);

            partitionsStore.setActivePartition(partition);
            doorsStore.setActiveDoor(partition);
            overdoorsStore.setActiveOverdoor(partition);
            mountsStore.setActiveMounts(partition);
            extraStore.setActiveExtra(partition);

            router.push({ path: `/calculation/2`, query: route.query });
        };

        const removePartition = (id: string) => {
            const spaceWithPartition = spaces.value.find((space) => {
                return Boolean(space.getPartitionById(id));
            });

            const allPartitions = spaceWithPartition?.getAllPartitions();

            if (spaceWithPartition?.id === activeSpace.value?.id) {
                if (allPartitions?.length === 1) {
                    partitionsStore.$reset();
                    doorsStore.$reset();
                    overdoorsStore.$reset();
                    mountsStore.$reset();

                    spacesStore.removeSpace(spaceWithPartition);

                    router.push({ path: `/calculation/2`, query: route.query });
                } else {
                    spaceWithPartition?.removePartition(id);
                    activeSpace.value?.removePartition(id);

                    const lastPartitions =
                        activeSpace.value?.getLastPartition();

                    setLastPartitionIsActive(lastPartitions);
                }
            } else {
                if (allPartitions?.length === 1) {
                    spacesStore.removeSpace(spaceWithPartition);
                } else {
                    spaceWithPartition?.removePartition(id);
                }
            }

            reloadSummary();
        };

        const setLastPartitionIsActive = (partition?: Partition) => {
            if (!partition) return;

            partitionsStore.setActivePartition(partition);
            doorsStore.setActiveDoor(partition);
            overdoorsStore.setActiveOverdoor(partition);
            mountsStore.setActiveMounts(partition);
            extraStore.setActiveExtra(partition);
        };

        const reloadSummary = async () => {
            if (estimateStore.estimate.shippingZip) {
                const distanceStore = useDistanceStore();
                await distanceStore.fetchDistance(
                    estimateStore.estimate.shippingZip,
                );
                deliveryStore.createDeliveryItems();
                deliveryStore.getOrderWeight();
            }
            summary.value = SummaryService.fromEstimate(
                estimate.value,
                spaces.value,
                deliveryItems.value,
            );
        };

        const loadEstimate = async (estimateCode: string) => {
            const estimateData = await getEstimate(estimateCode);
            resetAllStores();
            reloadPrices(estimateData.estimate?.info?.priceDateFormated, estimateData.estimate?.info?.priceDate);

            const estimate = EstimateConverterService.convertToNewFormat(
                estimateData.estimate,
                estimateData.calculation,
            );

            const delivery = EstimateConverterService.convertToNewDelivery(
                estimateData.estimate,
                estimateData.calculation,
            );
            const overheads = EstimateConverterService.convertToNewOverheads(
                estimateData.estimate,
                estimateData.calculation,
            );

            const spaces = EstimateConverterService.converToNewSpaces(
                estimateData.estimate,
                estimateData.calculation.subitems.items,
            );
            console.log("spaces:", spaces);
            overheadsStore.$restoreOverheads(overheads);
            deliveryStore.$restoreDelivery(delivery, estimateData.estimate?.info);
            estimateStore.$restoreEstimate(estimate);
            spacesStore.$restoreSpaces(spaces);
            partitionsStore.$restorePartitions();
            extraStore.$restoreExtra();
            doorsStore.$restoreDoors(spaces);
            overdoorsStore.$restoreOverdoors();
            mountsStore.$restoreMounts(spaces);
            installationStore.$restoreInstallation(estimate);

            reloadSummary();
        };

        const saveAsDraft = () => {
            estimateStore.persist();
            spacesStore.persist();
            partitionsStore.persist();
            doorsStore.persist();
            overdoorsStore.persist();
            deliveryStore.persist();
            extraStore.persist();
            mountsStore.persist();
            overheadsStore.persist();
        };

        const reloadPrices = (date, dateWithTime) => {
            dictStore.loadAllDictsByDate(date, dateWithTime);
        };

        return {
            summary,
            resetAllStores,
            removePartition,
            reloadSummary,
            addAnotherPartition,
            editPartition,
            addAnotherSpace,
            loadEstimate,
            saveAsDraft,
        };
    },
    { persist: false },
);
